import { sessionSnapshotSchema, type SessionSnapshot } from './types';

/**
 * KALICI OTURUM DEPOSU — ve neden localStorage.
 *
 * Burada iki sey saklanir:
 *   kural.refreshToken  -> yenileme jetonu (30 gun, her kullanimda donduruluyor)
 *   kural.session       -> oturum ozeti (kullanici, yetkiler, organizasyon…)
 *
 * ERISIM JETONU BURADA YOK ve hicbir zaman yazilmaz — yalniz bellekte tutulur
 * (bkz. src/api/client.ts). Kisa omurlu jetonu diske yazmak, kazanci olmayan
 * bir XSS yuzeyidir.
 *
 * YENILEME JETONU ICIN DURUST TAKAS:
 * localStorage'a erisen bir XSS yenileme jetonunu okuyabilir. httpOnly cerez
 * bunu engellerdi ama bu API cerez tabanli degil (mobil istemci ayni uclari
 * kullaniyor) ve CSRF ile SameSite'i ayrica cozmeyi gerektirirdi. Riski
 * kabul ediyoruz, cunku:
 *   - uygulama AYNI KOKENDEN servis ediliyor; ucuncu taraf betik yuklenmiyor,
 *   - CSP `script-src 'self'` + satir ici betige yalniz hash ile izin (bkz.
 *     deploy/kural-app-security-headers.conf, src/__tests__/csp-hash.test.ts);
 *     `unsafe-inline` YOK,
 *   - jeton CIHAZA BAGLI: baska bir cihaz kimliginden sunuldugunda sunucu
 *     hirsizlik sayar ve tum oturumlari dusurur,
 *   - jeton TEK KULLANIMLIK: her yenilemede donduruluyor, calinan bir kopya
 *     en fazla bir kez ise yarar ve ikinci kullanim hirsizligi gorunur kilar.
 * Yani XSS varsa zaten kaybedilmis durumdayiz; buradaki secim o senaryoyu
 * degistirmiyor, yalniz onu ucuz bir sekilde kotuye kullanilabilir yapmiyor.
 */

export const REFRESH_TOKEN_STORAGE_KEY = 'kural.refreshToken';
export const REFRESH_EXPIRES_STORAGE_KEY = 'kural.refreshExpiresAt';
export const SESSION_STORAGE_KEY = 'kural.session';

const readItem = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeItem = (key: string, value: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* gizli kip: oturum bu sekme boyunca bellekte yasar */
  }
};

const removeItem = (key: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* yok sayilir */
  }
};

export const readRefreshToken = (): string | null => readItem(REFRESH_TOKEN_STORAGE_KEY);

export const writeRefreshToken = (token: string, expiresAt?: number | string | null): void => {
  writeItem(REFRESH_TOKEN_STORAGE_KEY, token);
  if (expiresAt === undefined || expiresAt === null) return;
  writeItem(REFRESH_EXPIRES_STORAGE_KEY, String(expiresAt));
};

/**
 * Yenileme jetonunun suresi KANITLI olarak dolmus mu?
 *
 * Guvenlik kontrolu DEGIL — imzayi yalniz sunucu dogrular. Amaci, soguk
 * acilista canlandirilamayacagi kesin olan bir oturumu geri yuklemeyip
 * bosuna bir istek ve giris ekranina sicrama uretmemek.
 *
 * Bilincli olarak ACIK TARAFA duser: kayit yoksa ya da okunamiyorsa `false`
 * doner ve oturum eskisi gibi geri yuklenir. Buradaki bir hata kimseyi
 * hesabindan kilitleyemez.
 */
export const isRefreshKnownExpired = (): boolean => {
  const raw = readItem(REFRESH_EXPIRES_STORAGE_KEY);
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds <= 0) return false;
  return seconds * 1000 <= Date.now();
};

export const readSnapshot = (): SessionSnapshot | null => {
  const raw = readItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    // Sema ile dogrulanir: bozuk/kurcalanmis bir kayit "yetkili" bir duruma
    // cozulmemeli. Cozulemiyorsa oturum yok sayilir, kullanici giris yapar.
    const parsed = sessionSnapshotSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

export const writeSnapshot = (snapshot: SessionSnapshot): void => {
  try {
    writeItem(SESSION_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* yok sayilir */
  }
};

/**
 * Kalici oturum izlerinin TAMAMINI siler. Cihaz kimligi BILINCLI olarak kalir:
 * o oturumu degil cihazi tanimlar ve degistirmek bir sonraki yenilemeyi
 * hirsizlik gibi gosterir.
 */
export const clearPersistedSession = (): void => {
  removeItem(REFRESH_TOKEN_STORAGE_KEY);
  removeItem(REFRESH_EXPIRES_STORAGE_KEY);
  removeItem(SESSION_STORAGE_KEY);
};
